import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { TaskType, isGenerativeTask } from '@/shared/domain/taskTypes';
import { useCommercialModels } from '@/shared/hooks/useCommercialModels';
import CostEstimateFooter from '@/features/billing/components/CostEstimateFooter';
import { dataRetrievalEstimateParams } from '@/features/billing/utils/costEstimateParams';
import GenAiModalShell from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/GenAiModalShell';
import ProviderWarningBanner from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/ProviderWarningBanner';
import { analyzeProviderGate } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/providerGate';
import { createDefaultSettings } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/AdvancedSettingsSection';
import useDatasetNameField from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/useDatasetNameField';
import useGenerationTargets from '@/features/datasets/components/DataCollection/GenerateDataWithAI/useGenerationTargets';
import { formatRecordsBanner } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/flows/ClassificationFlow';
import { formatContentGenerationBanner } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/flows/ContentGenerationFlow';
import { formatSummarizationBanner } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/flows/SummarizationFlow';
import { EMPTY_NER_DATASET_BANNER_TEXT } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/constants';
import DefineTaskStep from '@/features/datasets/components/DataCollection/RetrievalAgent/steps/DefineTaskStep';
import OptionalSettingsStep from '@/features/datasets/components/DataCollection/RetrievalAgent/steps/OptionalSettingsStep';
import ReviewStep from '@/features/datasets/components/DataCollection/RetrievalAgent/steps/ReviewStep';
import {
    CONFIGURE_SUBTITLE,
    DEFAULT_RECORD_LIMIT,
    DEFINE_SUBTITLE,
    recordLimitErrorText,
} from '@/features/datasets/components/DataCollection/RetrievalAgent/constants';
import { providerModelItems } from '@/features/datasets/components/DataCollection/RetrievalAgent/providerOptions';
import {
    fetchRetrievalAgentSessions,
    fetchRetrievalDatasetNameSuggestion,
    retrievalTaskTypeLabel,
    sanitizeAgentModel,
    seedLabelNames,
    sessionOptionTitle,
    sessionToWizardPrefill,
} from '@/features/datasets/utils/retrievalAgentDataset';

/**
 * Dataset Retrieval Agent wizard (Figma Data Creation): Define Task (goal,
 * description, labels, Advanced settings, Exclude Datasets) →
 * Optional Settings → Review & Initiate.
 */
const RetrievalAgentModal = ({ visible, onCancel, onInitiate, usecase }) => {
    const {
        availableProviders,
        isLoading: isLoadingProviders,
        refresh: refreshCommercialModels,
    } = useCommercialModels();
    const { hasNoProviders, typeSafeOnly } = analyzeProviderGate(
        availableProviders,
        isLoadingProviders
    );

    // The catalog is cached in localStorage and refreshed on credential saves
    // in this tab only; re-fetch on open so a tab left open across a
    // credentials change never offers a provider without keys.
    useEffect(() => {
        if (visible) refreshCommercialModels?.();
    }, [visible, refreshCommercialModels]);
    const providersUnusable = hasNoProviders || typeSafeOnly;
    const modelItems = useMemo(() => providerModelItems(availableProviders), [availableProviders]);

    const [step, setStep] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [sessions, setSessions] = useState([]);
    const [loadedSessionTitle, setLoadedSessionTitle] = useState(undefined);
    const [goal, setGoal] = useState('');
    const [goalError, setGoalError] = useState(false);
    const [guidelinesError, setGuidelinesError] = useState(false);
    const [languages, setLanguages] = useState(['English']);
    const [editedNames, setEditedNames] = useState(null);
    const [labelDescriptions, setLabelDescriptions] = useState({});
    const [examples, setExamples] = useState([]);
    const [recordLimit, setRecordLimit] = useState(DEFAULT_RECORD_LIMIT);
    // The 100,000 ceiling is reported as soon as it is crossed; an empty input
    // only after Next was tried.
    const [recordLimitSubmitAttempted, setRecordLimitSubmitAttempted] = useState(false);
    const recordLimitInlineError = recordLimitErrorText(recordLimit, {
        submitAttempted: recordLimitSubmitAttempted,
    });
    const [settings, setSettings] = useState(createDefaultSettings());
    const [agentModel, setAgentModel] = useState(null);
    const [agentModelError, setAgentModelError] = useState(false);
    const [datasetsToExclude, setDatasetsToExclude] = useState([]);
    const [excludeFile, setExcludeFile] = useState(null);

    const { targets, totalRecords: existingRecords } = useGenerationTargets(usecase, visible);
    const { datasetName, onDatasetNameChange, datasetNameError, validateDatasetName } = useDatasetNameField({
        open: visible,
        usecaseId: usecase?._id,
        fetchSuggestion: fetchRetrievalDatasetNameSuggestion,
    });
    const seededNames = useMemo(() => seedLabelNames(usecase, targets), [usecase, targets]);
    const labelNames = editedNames ?? seededNames;
    const labelOptions = useMemo(
        () => [...new Set([...seededNames, ...labelNames])],
        [seededNames, labelNames]
    );

    const taskType = usecase?.task_type;
    const usecaseId = usecase?._id;
    const isNer = taskType === TaskType.NER;
    const isGenerative = isGenerativeTask(taskType);
    const isMultilabel = taskType === TaskType.classification && Boolean(usecase?.is_multilabel);
    const showLabels = !isGenerative;
    const labelHeading = isNer ? 'Entity types' : 'Active Labels';

    /** Records summary / empty-dataset copy, matching the Generate-with-AI banners. */
    let bannerText;
    if (taskType === TaskType.contentGeneration) {
        bannerText = formatContentGenerationBanner(existingRecords);
    } else if (isGenerative) {
        bannerText = formatSummarizationBanner(existingRecords);
    } else {
        bannerText = formatRecordsBanner(targets, isNer ? EMPTY_NER_DATASET_BANNER_TEXT : undefined);
    }

    useEffect(() => {
        if (!visible || Object.keys(labelDescriptions).length) return;
        const fromUsecase = usecase?.label_descriptions || {};
        if (Object.keys(fromUsecase).length) setLabelDescriptions({ ...fromUsecase });
    }, [visible, usecase, labelDescriptions]);

    useEffect(() => {
        if (!visible || !usecaseId) return undefined;
        let cancelled = false;
        fetchRetrievalAgentSessions(usecaseId).then((rows) => {
            if (!cancelled) setSessions(rows);
        });
        return () => {
            cancelled = true;
        };
    }, [visible, usecaseId]);

    const reset = () => {
        setStep(0);
        setIsSubmitting(false);
        setLoadedSessionTitle(undefined);
        setGoal('');
        setGoalError(false);
        setGuidelinesError(false);
        setLanguages(['English']);
        setEditedNames(null);
        setLabelDescriptions({});
        setExamples([]);
        setRecordLimit(DEFAULT_RECORD_LIMIT);
        setRecordLimitSubmitAttempted(false);
        setSettings(createDefaultSettings());
        setAgentModel(null);
        setAgentModelError(false);
        setDatasetsToExclude([]);
        setExcludeFile(null);
    };

    const handleClose = () => {
        reset();
        onCancel();
    };

    /** Deselecting a label clears its description and unassigns it from examples. */
    const handleLabelNamesChange = (next) => {
        const removed = labelNames.filter((name) => !next.includes(name));
        setEditedNames(next);
        if (!removed.length) return;
        setLabelDescriptions((prev) => {
            const nextDescriptions = { ...prev };
            removed.forEach((name) => delete nextDescriptions[name]);
            return nextDescriptions;
        });
        setExamples((prev) =>
            prev.map((row) => ({
                ...row,
                label: removed.includes(row.label) ? '' : row.label,
                labels: (row.labels || []).filter((name) => !removed.includes(name)),
            }))
        );
    };

    const removeLabel = (name) => handleLabelNamesChange(labelNames.filter((item) => item !== name));

    /** Prefill the whole wizard from a past session configuration. */
    const applySession = (session) => {
        if (!session) return;
        const prefill = sessionToWizardPrefill(session, { taskType });
        setLoadedSessionTitle(sessionOptionTitle(session));
        setGoal(prefill.goal);
        setGoalError(false);
        setGuidelinesError(false);
        if (prefill.languages.length) setLanguages(prefill.languages);
        if (showLabels && prefill.labelNames.length) setEditedNames(prefill.labelNames);
        setLabelDescriptions(prefill.labelDescriptions);
        setExamples(prefill.examples);
        setRecordLimit(prefill.recordLimit ?? DEFAULT_RECORD_LIMIT);
        setSettings((prev) => ({
            ...prev,
            domain: prefill.domain,
            toneStyles: prefill.toneStyles,
            textLength: prefill.textLength || prev.textLength,
            contentType: prefill.contentType || prev.contentType,
            customGuidelines: prefill.customGuidelines,
        }));
        // A stored provider that lost its credentials since the session ran
        // is dropped so the run falls back to the usecase default instead of
        // failing with "Provider ... not registered".
        const storedModel = sanitizeAgentModel(prefill.agentModel, availableProviders);
        if (storedModel) {
            const match = modelItems.find((item) => item.id === storedModel.name);
            setAgentModel({ ...storedModel, label: match?.label || storedModel.name });
            setAgentModelError(false);
        }
        setDatasetsToExclude(prefill.datasetsToExclude);
        setExcludeFile(null);
    };

    const validateStep0 = () => {
        const nextGoalError = !goal.trim();
        const nextGuidelinesError = !settings.customGuidelines.trim();
        const nextAgentModelError = !agentModel?.name;
        const datasetNameValid = validateDatasetName();
        setGoalError(nextGoalError);
        setGuidelinesError(nextGuidelinesError);
        setAgentModelError(nextAgentModelError);
        return !nextGoalError && !nextGuidelinesError && !nextAgentModelError && datasetNameValid;
    };

    const handlePrimary = async () => {
        if (step === 0) {
            if (validateStep0()) setStep(1);
            return;
        }
        if (step === 1) {
            setRecordLimitSubmitAttempted(true);
            if (!recordLimitErrorText(recordLimit, { submitAttempted: true })) setStep(2);
            return;
        }
        try {
            setIsSubmitting(true);
            await onInitiate({
                datasetName: datasetName.trim(),
                goal: goal.trim(),
                languages,
                labelNames: showLabels ? labelNames : [],
                labelDescriptions: showLabels ? labelDescriptions : {},
                examples,
                recordLimit,
                domain: settings.domain,
                agentModel,
                toneStyles: settings.toneStyles,
                textLength: settings.textLength,
                contentType: settings.contentType,
                customGuidelines: settings.customGuidelines,
                datasetsToExclude,
            });
            handleClose();
        } finally {
            setIsSubmitting(false);
        }
    };

    const warningBanner = providersUnusable ? (
        <ProviderWarningBanner featureName="Retrieval Agent" typeSafeOnly={typeSafeOnly} />
    ) : null;

    return (
        <GenAiModalShell
            open={visible}
            onClose={handleClose}
            title="Dataset Retrieval Agent"
            subtitle={step === 0 ? DEFINE_SUBTITLE : CONFIGURE_SUBTITLE}
            primaryLabel={step === 2 ? 'Initiate Agent' : 'Next'}
            onPrimary={handlePrimary}
            primaryDisabled={providersUnusable}
            primaryDisableWhenReadOnly={step === 2}
            isSubmitting={isSubmitting}
            showBack={step > 0}
            onBack={() => setStep(step - 1)}
            footerStart={
                <CostEstimateFooter
                    operation="data_retrieval"
                    label="data retrieval"
                    params={dataRetrievalEstimateParams(recordLimit)}
                />
            }
        >
            {warningBanner}
            {step === 0 ? (
                <DefineTaskStep
                    bannerText={bannerText}
                    datasetName={datasetName}
                    onDatasetNameChange={onDatasetNameChange}
                    datasetNameError={datasetNameError}
                    sessions={sessions}
                    loadedSessionTitle={loadedSessionTitle}
                    onLoadSession={applySession}
                    goal={goal}
                    goalError={goalError}
                    onGoalChange={(value) => {
                        setGoal(value);
                        setGoalError(false);
                    }}
                    customGuidelines={settings.customGuidelines}
                    customGuidelinesError={guidelinesError}
                    onCustomGuidelinesChange={(value) => {
                        setSettings((prev) => ({ ...prev, customGuidelines: value }));
                        setGuidelinesError(false);
                    }}
                    labelHeading={showLabels ? labelHeading : null}
                    labelOptions={labelOptions}
                    labelNames={showLabels ? labelNames : []}
                    onLabelNamesChange={handleLabelNamesChange}
                    domain={settings.domain}
                    onDomainChange={(value) => setSettings((prev) => ({ ...prev, domain: value }))}
                    agentModel={agentModel}
                    agentModelError={agentModelError}
                    onAgentModelChange={(value) => {
                        setAgentModel(value);
                        setAgentModelError(false);
                    }}
                    languages={languages}
                    onLanguagesChange={setLanguages}
                    toneStyles={settings.toneStyles}
                    onToneStylesChange={(value) => setSettings((prev) => ({ ...prev, toneStyles: value }))}
                    textLength={settings.textLength}
                    onTextLengthChange={(value) => setSettings((prev) => ({ ...prev, textLength: value }))}
                    contentType={settings.contentType}
                    onContentTypeChange={(value) => setSettings((prev) => ({ ...prev, contentType: value }))}
                    datasetsToExclude={datasetsToExclude}
                    onDatasetsToExcludeChange={setDatasetsToExclude}
                    excludeFile={excludeFile}
                    onExcludeFileChange={setExcludeFile}
                />
            ) : null}
            {step === 1 ? (
                <OptionalSettingsStep
                    showLabels={showLabels}
                    isNer={isNer}
                    isGenerative={isGenerative}
                    isMultilabel={isMultilabel}
                    labelNames={labelNames}
                    labelDescriptions={labelDescriptions}
                    onLabelDescriptionChange={(name, value) =>
                        setLabelDescriptions((prev) => ({ ...prev, [name]: value }))
                    }
                    onRemoveLabel={removeLabel}
                    examples={examples}
                    onExamplesChange={setExamples}
                    recordLimit={recordLimit}
                    recordLimitErrorText={recordLimitInlineError}
                    onRecordLimitChange={setRecordLimit}
                />
            ) : null}
            {step === 2 ? (
                <ReviewStep
                    goal={goal.trim()}
                    customGuidelines={settings.customGuidelines.trim()}
                    languages={languages}
                    taskTypeLabel={retrievalTaskTypeLabel(usecase)}
                    modelLabel={agentModel?.label || agentModel?.name}
                    recordLimit={recordLimit}
                />
            ) : null}
        </GenAiModalShell>
    );
};

RetrievalAgentModal.propTypes = {
    visible: PropTypes.bool.isRequired,
    onCancel: PropTypes.func.isRequired,
    onInitiate: PropTypes.func.isRequired,
    usecase: PropTypes.object,
};

export default RetrievalAgentModal;
